import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFocusEffect } from "@react-navigation/native";
import styled from "styled-components/native";
import { Container, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { DeviceEventEmitter } from "react-native";
import RequestViewParentCard from "@components/organisms/RequestViewParentCard";
import { ButtonType } from "@components/atoms/Button/types";
import { getRequestType, isValidValue, RequestTypes } from "./utils/utils";
import BmhForm from "@components/molecules/BMH/BmhForm";
import {
  useUpdateBulkRequest,
  useUpdateRequestHistory,
} from "@apis/requestHistory";
import { ToastTypes, showToast } from "@components/molecules/VDBToast/util";
import Loader from "@components/atoms/Loader";
import { ActiveClickTypes } from "./types";
import RequestConfirmPopup from "./utils/RequestConfirmPopup";
import { useTranslation } from "react-i18next";
import Popup from "@components/molecules/PopUp";
import { messageType } from "@components/molecules/InputPopUp/types";
import LayoutContainer from "@components/layout/LayoutContainer";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import WebPageHeader from "@components/molecules/WebPageHeader/WebPageHeader";
import { SelectedItemsProvider } from "../../contexts/SelectedItemsContext";
import {
  BulkStateProvider,
  useBulkRequestState,
} from "@scenes/requests/context/BulkStateContext";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import VDBIcon, { IconsType, IconsVariant } from "@components/icons/VDBIcon";
import { ProductCategory, ProductTypeCommon } from "@utils/constants";
import {
  useConsolidatedStoneTypes,
  useProducts,
} from "@redux/slices/globalSlice";
import useScreenOptionsHooks from "../../hooks/useScreenOptionsHooks";
import {
  DynamicFormProvider,
  useDynamicFormState,
} from "@scenes/bulk-request/context/DynamicFormContext";
import { filterAndSortFormData } from "@components/molecules/DynamicForm/utils";
import { useUnifiedProductPagesSlice } from "@redux/slices/unifiedProductPages";
import DynamicForm from "@components/molecules/DynamicForm/DynamicForm";
import { useSelector } from "react-redux";
import ButtonViewContainer from "./ButtonViewContainer";
import {
  isPair,
  isParcel,
  isMeleeItem,
} from "@utils/product/productValidators";
import { SearchFilterTypeEnumLowercase } from "@components/atoms/Filters/utils";
import { useUserCurrency } from "../../hooks/getCurrency";
import currency from "currency.js";
import { css } from "styled-components/native";
import { getProductName } from "@utils/product/productNames";
import { DefaultTheme } from "styled-components/native";

const defaultDataBuy = {
  comments: "",
  memoComments: "",
  buyComments: "",
  holdComments: "",
  ctOffer: 0,
  discountPercent: 0,
  priceOffer: 0,
  selectedRadio: "COD",
  payWithIn: 30,
};

const defaultDataMemo = {
  memoComments: "",
};

const defaultDataHold = {
  holdComments: "",
  hold_hours: "",
};

const RequestModifyScreen = ({ route, navigation }: any) => {
  const { t } = useTranslation();
  const { theme, typography } = useScreenOptionsHooks();
  const userInfo = useUserInfo();
  const buttonActionRef = useRef(null);

  const {
    stockItemRequest = {},
    productType,
    externalPriceMode,
    checkboxBlock,
    isTotalOfferDiscountApplied,
    childValues,
  } = route?.params ?? {};
  const mPriceMode = externalPriceMode
    ? externalPriceMode
    : userInfo.getUserPriceMode();
  const customForm = useSelector(
    (state: any) => state.globalData?.app_settings?.custom_form
  );
  const allProducts = useProducts();
  const getProducts = useMemo(() => allProducts, [allProducts]);
  const matchedProduct = getProducts?.find(
    (item) => (item as any)?.key === productType?.productType
  );
  const icon = (matchedProduct as any)?.sub_type?.find(
    (subType: any) =>
      subType.name === stockItemRequest?.product_sub_type?.toLowerCase()
  )?.icon;

  const iconData = matchedProduct
    ? icon?.data || (matchedProduct as any)?.icon?.data
    : null;

  const requestType: string = getRequestType(stockItemRequest?.request_type);
  const [showPopup, setShowPopup] = useState(false);
  const [showConfirmModifyPopup, setShowConfirmModifyPopup] = useState(false);
  const [disableRequest, setDisableRequest] = useState(false);
  const [activeClick, setActiveClick] = useState<ActiveClickTypes>(
    ActiveClickTypes.WITHDRAW
  );
  const { isDirtyForm } = useDynamicFormState();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);

  const [screenTitle, setScreenTitle] = useState("");
  const isBulkRequest = !!stockItemRequest?.children;
  const { items } = (useBulkRequestState() as any) || {};
  const [singleItemRequest, setSingleItemRequest] = useState(stockItemRequest);

  useEffect(() => {
    if (isBulkRequest) return;
    setSingleItemRequest(stockItemRequest);
  }, [stockItemRequest, isBulkRequest]);
  const unifiedProductPagesSlice = useUnifiedProductPagesSlice();
  const consolidatedStoneType = useConsolidatedStoneTypes();
  const unifiedProductData = isPair(
    stockItemRequest?.[stockItemRequest?.stock_item_type]
  )
    ? unifiedProductPagesSlice?.unifiedProductData?.[productType?.productType]
        ?.pair?.requests
    : isCustomApp &&
      isParcel(stockItemRequest?.[stockItemRequest?.stock_item_type])
    ? theme.isMobile
      ? unifiedProductPagesSlice?.unifiedProductData?.[productType?.productType]
          ?.parcels?.mobile_parcels?.requests
      : unifiedProductPagesSlice?.unifiedProductData?.[productType?.productType]
          ?.parcels?.requests
    : unifiedProductPagesSlice?.unifiedProductData?.[productType?.productType]
        ?.requests;
  const [itemList, setItemList] = useState([]);

  const buildItemList = useCallback(
    () =>
      stockItemRequest?.children?.map((child: any) => ({
        ...child[productType?.productType],
        updated_price_per_carat: child.offer_price_per_carat,
        updated_total_sales_price: child.offer_total_sales_price,
        updated_discount_percent: child.offer_discount_percent,
        IndividualItemEdit: child.IndividualItemEdit,
        comments: child.comments,
        ...(child.quantity && { quantity: child.quantity }),
        ...(child.offer_size && { offer_size: child.offer_size }),
        ...(child.offer_total_sales_price && {
          offer_total_sales_price: child.offer_total_sales_price,
        }),
      })) || [],
    [stockItemRequest, productType]
  );

  useEffect(() => {
    setItemList(buildItemList());
  }, [buildItemList]);

  const precision =
    productType?.productType === ProductCategory.JEWELRY ||
    (userInfo?.roundUpPrices() && !isMeleeItem(itemList?.[0]))
      ? 0
      : 2;

  const isLooseParcel =
    itemList?.length > 0
      ? (itemList?.[0] as any)?.diamond_category ===
        SearchFilterTypeEnumLowercase.LOOSE_PARCEL
      : false;
  const userCurrency = useUserCurrency();
  const formData =
    filterAndSortFormData(
      isBulkRequest
        ? unifiedProductData?.formData[requestType.toLocaleLowerCase()]
        : unifiedProductData?.singleItemForm[requestType.toLocaleLowerCase()],
      mPriceMode
    ) ?? [];

  useEffect(() => {
    const isPurchasingAgent =
      stockItemRequest?.bmh_request_type === "purchasing_agent";
    const requestTypeText = isPurchasingAgent
      ? ""
      : getRequestType(stockItemRequest?.request_type);
    const title = `${t(
      "requestHistoryDetail.title.modify.text"
    )} ${requestTypeText} ${t("requestHistoryDetail.title.request.text")}`;
    setScreenTitle(title);
  }, []);

  const showConfirmPopup = (click: ActiveClickTypes) => {
    setShowPopup(true);
    setActiveClick(click);
  };
  const isValid = isValidValue(stockItemRequest.offer_total_sales_price);
  const modifyRequest = useUpdateRequestHistory(stockItemRequest.id);
  const modifyBulkRequest = useUpdateBulkRequest();

  useEffect(() => {
    const resetItemArray = DeviceEventEmitter.addListener(
      "RESET_BULK_ITEM_ARRAY",
      () => {
        const removedItems = removeUpdatedFields();
        setItemList(removedItems as any);
      }
    );

    return () => {
      resetItemArray.remove();
    };
  }, [itemList]);

  const removeUpdatedFields = () => {
    return itemList.map(({ item, ...rest }: any) => {
      return Object.fromEntries(
        Object.entries({ item, ...rest }).filter(
          ([key]) =>
            ![
              "updated_price_per_carat",
              "updated_total_sales_price",
              "updated_discount_percent",
            ].includes(key)
        )
      );
    });
  };

  const [dataBuy, setDataBuy] = useState(defaultDataBuy);
  const [dataMemo, setDataMemo] = useState(defaultDataMemo);
  const [dataHold, setDataHold] = useState(defaultDataHold);
  const [isDirty, setIsDirty] = useState(false);

  // Reset the screen back to its initial state whenever it loses focus, so it
  // is "fresh" the next time it is opened. This replaces the v6
  // `unmountOnBlur: true` screen option, which was removed in React Navigation
  // v7 (the screen now stays mounted, so we reset state manually on blur).
  useFocusEffect(
    useCallback(
      () => () => {
        setShowPopup(false);
        setShowConfirmModifyPopup(false);
        setDisableRequest(false);
        setActiveClick(ActiveClickTypes.WITHDRAW);
        setSingleItemRequest(stockItemRequest);
        setItemList(buildItemList());
        setDataBuy(defaultDataBuy);
        setDataMemo(defaultDataMemo);
        setDataHold(defaultDataHold);
        setIsDirty(false);
      },
      [stockItemRequest, buildItemList]
    )
  );

  const saveBulkItemDetails = (id: number, details: any) => {
    if (!isBulkRequest) {
      // Single item request - update singleItemRequest directly
      setSingleItemRequest((prev: any) => ({
        ...prev,
        ...(details?.quantity && {
          quantity: Number(details?.quantity),
        }),
      }));
      return;
    }

    // Bulk request - update itemList
    setItemList((prevList: any) => {
      const nextList = prevList.map((item: any) => {
        if (item?.id !== id) return item; // skip if not matching

        const updatedItem = {
          ...item,
          ...((requestType === RequestTypes.Buy ||
            requestType === RequestTypes.Quote) && {
            updated_price_per_carat: details?.pricePerOffer?.replace(/,/g, ""),
            updated_total_sales_price: details?.totalOfferPrice?.replace(
              /,/g,
              ""
            ),
            updated_discount_percent: details?.totalOfferDiscount?.replace(
              /,/g,
              ""
            ),
          }),
          // Update quantity / offer size if provided
          ...(details?.quantity && {
            quantity: details?.quantity.replace(/,/g, ""),
          }),
          ...(details?.offer_size && {
            offer_size: details?.offer_size.replace(/,/g, ""),
          }),
          ...(details?.offer_total_sales_price && {
            updated_total_sales_price: details?.offer_total_sales_price.replace(
              /,/g,
              ""
            ),
          }),
          updated_comments: details?.comments ?? item?.updated_comments,
        };

        return updatedItem;
      });

      return nextList;
    });
  };

  const handleSuccess = (msg?: string) => {
    if (msg) showToast("Success", msg, ToastTypes.SUCCESS, true, true);
    if (navigation.canGoBack()) navigation.goBack();
  };

  const handleModifyRequest = () => {
    if (buttonActionRef.current) {
      (buttonActionRef.current as any).handleRequest();
    }
  };

  const totalUpdatedSalesPrice = useMemo(() => {
    return itemList?.reduce((sum, item) => {
      const value = Number((item as any)?.updated_total_sales_price) || 0;
      return sum + value;
    }, 0);
  }, [itemList]);

  // Guard: return null if required params are missing (e.g., during navigator remount on price mode change)
  if (!stockItemRequest || !productType) {
    return null;
  }

  return (
    <>
      <SelectedItemsProvider>
        <MainContainer>
          <WebPageHeader
            title={screenTitle}
            hasUnsavedChanges={isDirty || (items?.length && items?.length > 0)}
          />
          <KeyboardAwareScrollView
            bottomOffset={20}
            keyboardShouldPersistTaps="always"
            style={{
              paddingHorizontal: theme.isMobile
                ? SpaceType.Basic
                : SpaceType.Double,
            }}
          >
            <Container maxWidth={1008} noPadding>
              <Spacer size={SpaceType.Double} />
              <CardContainer>
                <ProductListTopContainer background={productType.theme}>
                  <VDBIcon
                    variant={
                      icon?.type === IconsType.img_url ||
                      icon?.type === IconsType.url ||
                      (matchedProduct as any)?.icon?.type ===
                        IconsType.img_url ||
                      (matchedProduct as any)?.icon?.type === IconsType.url
                        ? IconsVariant.CustomUrl
                        : IconsVariant.VDBCustom
                    }
                    name={iconData}
                    uri={iconData}
                    size={SpaceType.Big}
                    color={
                      theme[
                        `${productType.theme}-06` as keyof DefaultTheme
                      ] as string
                    }
                  />
                  <Spacer size={SpaceType.ExtraSmall} />
                  <VDBText
                    type={typography["H3-M"]}
                    color={
                      theme[
                        `${productType.theme}-06` as keyof DefaultTheme
                      ] as string
                    }
                  >
                    {stockItemRequest?.[productType.productType]?.sub_type ===
                    ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE
                      ? getProductName(ProductCategory.LABGROWN_JEWELRY, t)
                      : `${t(stockItemRequest.type_text)} ${
                          consolidatedStoneType.enabled
                            ? ""
                            : stockItemRequest.product_sub_type
                            ? stockItemRequest.product_sub_type
                            : ""
                        }`}
                  </VDBText>
                </ProductListTopContainer>
                <Spacer size={SpaceType.Double} />
                <ItemsContainer>
                  {customForm ? (
                    <DynamicForm
                      form={formData}
                      fromRequestDetail={true}
                      stockItemRequest={stockItemRequest}
                      hideSubmitButton={true}
                      itemList={itemList}
                      setItemList={setItemList}
                      fromRequestModify={true}
                      skipHasNullDiscount={false}
                      {...(!isBulkRequest && {
                        itemDetail: {
                          ...stockItemRequest[productType.productType],
                          ...(stockItemRequest?.quantity && {
                            quantity: stockItemRequest.quantity?.toString(),
                          }),
                          ...(stockItemRequest?.offer_size && {
                            offer_size: stockItemRequest.offer_size,
                          }),
                        },
                      })}
                    />
                  ) : (
                    <BmhForm
                      selectedOption={requestType?.toLowerCase()}
                      canRequestAgain={{ can_request_again: true }}
                      stockItemRequest={stockItemRequest}
                      itemDetail={stockItemRequest[productType.productType]}
                      onDataBuyChange={setDataBuy}
                      onDataMemoChange={setDataMemo}
                      onDataHoldChange={setDataHold}
                      setIsDirty={setIsDirty}
                      disableRequestButton={setDisableRequest}
                      onModify={true}
                      fromRequestDetail={true}
                      isBulkRequest={isBulkRequest}
                      dataHold={dataHold}
                    />
                  )}
                  <RequestViewParentCard
                    stockItemRequest={
                      isBulkRequest ? stockItemRequest : singleItemRequest
                    }
                    productType={productType}
                    isModifyScreen={true}
                    validValue={isValid}
                    itemList={itemList}
                    saveBulkItemDetails={saveBulkItemDetails}
                    showListPrice={checkboxBlock === false ? false : true}
                    isTotalOfferDiscountApplied={isTotalOfferDiscountApplied}
                    childValues={childValues}
                    onClickSupplierInfo={() => {}}
                    setDisabledButton={() => {}}
                  />
                </ItemsContainer>
              </CardContainer>
              <Spacer size={SpaceType.ExtraLarge} />
              {isLooseParcel && !theme.isMobile && (
                <>
                  <LooseParcelEstimatedPriceContainer>
                    <VDBText color={theme["core-06"]} type={typography["H3-R"]}>
                      {t(
                        "requestHistoryDetail.modify.totalEstimatedPrice.text"
                      )}
                    </VDBText>
                    <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
                      {Number(totalUpdatedSalesPrice) > 0
                        ? currency(totalUpdatedSalesPrice, {
                            symbol: userCurrency,
                            precision: precision,
                          }).format()
                        : "-"}
                    </VDBText>
                  </LooseParcelEstimatedPriceContainer>
                  <Spacer size={SpaceType.Extra} />
                </>
              )}
              {!theme.isMobile && (
                <>
                  <ButtonViewContainer
                    stockItemRequest={
                      isBulkRequest ? stockItemRequest : singleItemRequest
                    }
                    customForm={customForm}
                    productType={productType}
                    isBulkRequest={isBulkRequest}
                    requestType={requestType}
                    showConfirmPopup={showConfirmPopup}
                    modifyRequest={
                      isBulkRequest ? modifyBulkRequest : modifyRequest
                    }
                    navigation={navigation}
                    dataBuy={dataBuy}
                    dataMemo={dataMemo}
                    dataHold={dataHold}
                    itemList={customForm ? itemList : items}
                    priceMode={mPriceMode}
                    disableRequest={disableRequest}
                    setShowConfirmModifyPopup={setShowConfirmModifyPopup}
                    ref={buttonActionRef}
                    setIsDirty={setIsDirty}
                  />
                  <Spacer size={SpaceType.Size_80} />
                </>
              )}
            </Container>
          </KeyboardAwareScrollView>

          {theme.isMobile && (
            <>
              {isLooseParcel && (
                <>
                  <LooseParcelEstimatedPriceContainer>
                    <VDBText color={theme["core-06"]} type={typography["H3-R"]}>
                      {t(
                        "requestHistoryDetail.modify.totalEstimatedPrice.text"
                      )}
                    </VDBText>
                    <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
                      {Number(totalUpdatedSalesPrice) > 0
                        ? currency(totalUpdatedSalesPrice, {
                            symbol: userCurrency,
                            precision: precision,
                          }).format()
                        : "-"}
                    </VDBText>
                  </LooseParcelEstimatedPriceContainer>
                  <Spacer size={SpaceType.Extra} />
                </>
              )}
              <ButtonViewContainer
                stockItemRequest={
                  isBulkRequest ? stockItemRequest : singleItemRequest
                }
                customForm={customForm}
                productType={productType}
                isBulkRequest={isBulkRequest}
                requestType={requestType}
                showConfirmPopup={showConfirmPopup}
                modifyRequest={
                  isBulkRequest ? modifyBulkRequest : modifyRequest
                }
                navigation={navigation}
                dataBuy={dataBuy}
                dataMemo={dataMemo}
                dataHold={dataHold}
                itemList={customForm ? itemList : items}
                priceMode={mPriceMode}
                disableRequest={disableRequest}
                setShowConfirmModifyPopup={setShowConfirmModifyPopup}
                ref={buttonActionRef}
                setIsDirty={setIsDirty}
              />
            </>
          )}

          {showPopup && (
            <RequestConfirmPopup
              stockItemRequest={
                isBulkRequest ? stockItemRequest : singleItemRequest
              }
              activeClick={activeClick as any}
              onPopupShowChange={setShowPopup}
              onSubmitChange={() => {
                handleSuccess();
              }}
            />
          )}

          {showConfirmModifyPopup && (
            <Popup
              type={messageType.Warning}
              styleKey="Body-R"
              title={t("requestHistoryDetail.modify.requestPopup.title.text")}
              buttons={[
                {
                  type: ButtonType.primary,
                  loading: isBulkRequest
                    ? modifyBulkRequest.isLoading
                    : modifyRequest.isLoading,
                  disabled: isBulkRequest
                    ? modifyBulkRequest.isLoading
                    : modifyRequest.isLoading,
                  title:
                    Number(stockItemRequest?.offer_total_sales_price) ===
                    Number(dataBuy.priceOffer)
                      ? t("requestHistoryDetail.buttons.submit.text")
                      : t("requestHistoryDetail.buttons.accept.text"),
                  onPressAction: () => handleModifyRequest(),
                },
                {
                  type: ButtonType.secondary,
                  title: t(
                    "requestHistoryDetail.requestPopup.cancelButton.text"
                  ),
                  onPressAction: () => {
                    setShowConfirmModifyPopup(false);
                  },
                },
              ]}
              hasChildren={true}
            >
              {isDirty ||
              isDirtyForm ||
              (items?.length && items?.length > 0) ? (
                <VDBText>
                  {t(
                    "requestHistoryDetail.modify.requestPopup.description.text"
                  )}
                </VDBText>
              ) : (
                <VDBText>
                  {t(
                    "requestHistoryDetail.modify.requestPopup.notupdatedDescription.text"
                  )}
                </VDBText>
              )}
            </Popup>
          )}
          {(isBulkRequest
            ? modifyBulkRequest.isLoading
            : modifyRequest.isLoading) && <Loader />}
        </MainContainer>
      </SelectedItemsProvider>
    </>
  );
};

const MainContainer = styled(LayoutContainer)`
  flex: 1;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const CardContainer = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  border: 1px solid ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const ItemsContainer = styled.View`
  padding-bottom: ${SpaceType.Basic}px;
  padding-left: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? `${SpaceType.Basic}px` : `${SpaceType.Double}px`};
  padding-right: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? `${SpaceType.Basic}px` : `${SpaceType.Double}px`};
`;

const ProductListTopContainer = styled.View<{ background: string }>`
  background-color: ${({
    background,
    theme,
  }: {
    background: string;
    theme: DefaultTheme;
  }) => theme[`${background}-03` as keyof DefaultTheme] as string};
  padding: ${SpaceType.Extra}px ${SpaceType.Extra}px;
  flex-direction: row;
  align-items: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const LooseParcelEstimatedPriceContainer = styled.View`
  flex-direction: row;
  height: 56px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  border: 1px solid ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  justify-content: space-between;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 16px;
  padding-right: 16px;
  align-items: center;
  ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile &&
    css`
      margin-left: ${SpaceType.Basic}px;
      margin-right: ${SpaceType.Basic}px;
    `};
`;

export default (props: any) => {
  // Force a full remount (fresh state + providers) whenever the screen is
  // opened for a different item. React Navigation reuses the same mounted
  // instance and only swaps `route.params`, so state seeded on mount (e.g.
  // `singleItemRequest`, the screen title, and the form contexts) would
  // otherwise keep showing the previously opened item's data. This replaces the
  // v6 `unmountOnBlur` behaviour that was removed in React Navigation v7.
  const params = props?.route?.params ?? {};
  const remountKey = [
    params?.stockItemRequest?.id,
    params?.productType?.productType,
    params?.stockItemRequest?.request_type,
  ]
    .filter((part) => part !== undefined && part !== null)
    .join("-");

  const hasRequiredParams = !!params?.stockItemRequest && !!params?.productType;

  if (!hasRequiredParams) {
    return null;
  }

  return (
    <BulkStateProvider key={remountKey || "request-modify"}>
      <DynamicFormProvider>
        <RequestModifyScreen {...props} />
      </DynamicFormProvider>
    </BulkStateProvider>
  );
};
